/**
 * TAN-8655 — Título de página por ruta (WCAG 2.4.2).
 */

import { useContext, useEffect } from 'react';

import { PageTitleContext } from 'app/router/PageTitleContext';
import { composeTitleSegments } from 'app/router/pageTitleUtils';

/**
 * Mantiene `document.title` sincronizado con la ruta activa.
 *
 * Lee del `PageTitleContext`, que ya resolvió la ruta: aquí no se vuelve a recorrer la lista.
 *
 * Escribe **siempre** el título de la ruta, también en las que lo delegan en su página
 * (`isTitleOwnedByPage`). Parecería que ahí debería callarse para no pisar a `<PageTitle>`, pero
 * suprimir esta primera escritura abre dos huecos:
 *
 * - Las páginas se cargan con `React.lazy`, así que `<PageTitle>` no monta hasta que baja su chunk.
 *   Sin esta escritura, durante toda esa ventana —segundos en una conexión lenta, indefinidamente
 *   si el chunk falla— la pestaña seguiría anunciando la pantalla ANTERIOR: peor que un genérico, y
 *   justo durante la navegación que este ticket quiere arreglar.
 * - Si una página marcada sale por un `return` temprano sin llegar a pintar `<PageTitle>`, no
 *   escribiría nadie.
 *
 * Escribiendo el genérico y dejando que la página lo refine, el peor caso es un título correcto
 * pero poco específico. El orden está garantizado: este efecto vive en un componente hermano que se
 * renderiza antes del `<Switch>`, así que dispara antes que el de la página; y como ambos dependen
 * de `routeTitle`, un cambio de idioma los vuelve a disparar en ese mismo orden.
 */
function useRouteChangeHandler(): void {
  const { appName, routeTitle } = useContext(PageTitleContext);

  useEffect(() => {
    const nextTitle = composeTitleSegments([routeTitle, appName]);

    // Solo se escribe si hay algo que escribir: sobrescribir con cadena vacía dejaría la pestaña
    // en blanco (y un anuncio vacío en lectores de pantalla) en vez del título de `index.html`.
    if (nextTitle) {
      document.title = nextTitle;
    }
  }, [appName, routeTitle]);
}

export default useRouteChangeHandler;
